import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { MAX_SECTION_JUDGE_SEATS, suggestedQualityJudging } from "score-common";

// #3141 (follow-up from #923, docs/score/scoring-rules.md section 4c) -- the
// required "Set judging topology" prompt shown on class open when reconcile
// has flagged `Class.qualityJudgingUnset`. An unset topology silently shows and
// scores every movement, and reconcile cannot know the topology (it depends on
// the panel actually seated), so the admin must declare it.
//
// Pre-fill (suggestedQualityJudging, score-common/utils/quality-topology.js):
// two-judge, quality position E ONLY for Children (FEI Judging Manual p. 54
// "preferably E"), a neutral E/B choice for 7yo (p. 67 gives no preference),
// always two-judge (the FVD panel cannot tell one seated judge from two, so it
// is never consulted; technical-only is one Select change). Uses the same
// E/B picker values as the full T/Q config, and saves through the same
// onSaveQualityJudging (PUT class quality-judging) the config does.

export default function QualityJudgingPrompt({ classItem, test, saving, onSave }) {
  const { t } = useTranslation("scoreadmin");
  const c = classItem;
  // Never derive the topology from FVD's judgeAssignments: FVD emits ONE judge
  // at C for every T/Q pseudo-class, so a real two-judge class would pre-fill
  // technical-only (scoring-rules.md 4c answer 3). Technical-only is one Select
  // change away.
  // #3372: seats per section (1-5). Default one technical + one quality, the
  // national 7yo/Children two-judge shape; a 3-5 judge panel raises either.
  const [draft, setDraft] = useState(() => ({
    ...suggestedQualityJudging(test),
    technicalSeats: 1,
    qualitySeats: 1,
  }));
  const twoJudge = draft.topology === "two-judge";
  // E/B is required for two-judge: for 7yo the pre-fill is deliberately empty.
  const canSave = !saving && (!twoJudge || draft.qualityPosition === "E" || draft.qualityPosition === "B");

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      data-testid={`tq-unset-prompt-${c.id}`}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {t("tq.unsetHeading")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {t("tq.unsetHelp")}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
        <FormControl size="small" sx={{ width: 280 }}>
          <InputLabel id={`${c.id}-tq-unset-topology-label`}>{t("tq.topology")}</InputLabel>
          <Select
            labelId={`${c.id}-tq-unset-topology-label`}
            label={t("tq.topology")}
            value={draft.topology}
            disabled={saving}
            inputProps={{ "data-testid": `tq-unset-topology-${c.id}` }}
            onChange={(e) => setDraft((d) => ({ ...d, topology: e.target.value }))}
          >
            <MenuItem value="two-judge">{t("tq.twoJudge")}</MenuItem>
            <MenuItem value="technical-only">{t("tq.technicalOnly")}</MenuItem>
          </Select>
        </FormControl>
        {twoJudge && (
          <FormControl size="small" sx={{ width: 200 }} required>
            <InputLabel id={`${c.id}-tq-unset-pos-label`}>{t("tq.qualityPosition")}</InputLabel>
            <Select
              labelId={`${c.id}-tq-unset-pos-label`}
              label={t("tq.qualityPosition")}
              value={draft.qualityPosition ?? ""}
              disabled={saving}
              inputProps={{ "data-testid": `tq-unset-position-${c.id}` }}
              onChange={(e) => setDraft((d) => ({ ...d, qualityPosition: e.target.value || null }))}
            >
              <MenuItem value="">
                <em>{t("tq.unsetChoosePosition")}</em>
              </MenuItem>
              <MenuItem value="E">E</MenuItem>
              <MenuItem value="B">B</MenuItem>
            </Select>
            <FormHelperText>{t("tq.qualityPositionHelp")}</FormHelperText>
          </FormControl>
        )}
        {twoJudge &&
          ["technical", "quality"].map((section) => {
            const key = `${section}Seats`;
            return (
              <FormControl key={section} size="small" sx={{ width: 160 }}>
                <InputLabel id={`${c.id}-tq-unset-${section}-seats-label`}>{t(`tq.${key}`)}</InputLabel>
                <Select
                  labelId={`${c.id}-tq-unset-${section}-seats-label`}
                  label={t(`tq.${key}`)}
                  value={String(draft[key])}
                  disabled={saving}
                  inputProps={{ "data-testid": `tq-unset-${section}-seats-${c.id}` }}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: Number(e.target.value) }))}
                >
                  {Array.from({ length: MAX_SECTION_JUDGE_SEATS }, (_, i) => i + 1).map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          })}
        <Button
          variant="contained"
          disabled={!canSave}
          data-testid={`tq-unset-save-${c.id}`}
          onClick={() =>
            onSave(c.id, {
              topology: draft.topology,
              qualityPosition: twoJudge ? draft.qualityPosition : null,
              // Only sent when the panel is bigger than the 1 + 1 default, so
              // the common case saves exactly what it always did.
              ...(twoJudge && draft.technicalSeats > 1 ? { technicalSeats: draft.technicalSeats } : {}),
              ...(twoJudge && draft.qualitySeats > 1 ? { qualitySeats: draft.qualitySeats } : {}),
            })
          }
        >
          {t("tq.unsetSave")}
        </Button>
      </Box>
    </Alert>
  );
}
